/**
 * Product details — desktop underline tabs / mobile accordion.
 *
 * Relocates tab triggers into a shared tablist on desktop. On mobile, leaves
 * triggers on each panel as accordion headers (Figma Details-mobile) and uses
 * button + region roles instead of tab / tabpanel.
 * Hides empty tabs (data-empty="true") on the storefront; keeps them in
 * design mode. Keyboard: Arrow Left/Right (desktop), Arrow Up/Down (mobile),
 * Home, End. One panel active at a time.
 */
(() => {
  const TAG = 'product-details';
  const DESKTOP_QUERY = '(min-width: 768px)';

  class ProductDetails extends HTMLElement {
    #panels = [];
    #triggers = [];
    #triggersHost = null;
    #mediaQuery = window.matchMedia(DESKTOP_QUERY);
    #activeId = null;

    connectedCallback() {
      this.#triggersHost = this.querySelector('[data-product-details-triggers]');
      this.#panels = Array.from(this.querySelectorAll('[data-product-details-panel]'));

      if (this.#panels.length === 0) return;

      this.#collectTriggers();
      this.#syncLayout();
      this.#bindEvents();
      this.#mediaQuery.addEventListener('change', this.#handleMediaQueryChange);
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);

      const initial =
        this.#panels.find((panel) => panel.classList.contains('is-active')) ||
        this.#visiblePanels()[0];

      this.classList.add('is-ready');

      if (initial) {
        this.#activate(initial.getAttribute('data-block-id'));
      }
    }

    disconnectedCallback() {
      for (const trigger of this.#triggers) {
        trigger.removeEventListener('click', this.#handleTriggerClick);
        trigger.removeEventListener('keydown', this.#handleKeydown);
      }

      this.#mediaQuery.removeEventListener('change', this.#handleMediaQueryChange);
      document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    }

    get #isDesignMode() {
      return this.dataset.designMode === 'true';
    }

    get #isDesktop() {
      return this.#mediaQuery.matches;
    }

    #visiblePanels() {
      if (this.#isDesignMode) return this.#panels;

      return this.#panels.filter((panel) => panel.dataset.empty !== 'true');
    }

    #findTrigger(panel) {
      const owned = panel.querySelector(':scope > [data-product-details-trigger]');

      if (owned) return owned;

      const blockId = panel.getAttribute('data-block-id');

      if (!blockId || !this.#triggersHost) return null;

      return this.#triggersHost.querySelector(
        `[data-product-details-trigger][data-block-id="${blockId}"]`
      );
    }

    #findBody(panel) {
      return panel.querySelector(':scope > [data-product-details-body]');
    }

    #collectTriggers() {
      this.#triggers = [];

      for (const panel of this.#panels) {
        const trigger = this.#findTrigger(panel);
        const isEmpty = panel.dataset.empty === 'true';

        if (!trigger) continue;

        if (!this.#isDesignMode && isEmpty) {
          trigger.hidden = true;
          panel.hidden = true;
          continue;
        }

        trigger.hidden = false;
        panel.hidden = false;
        this.#triggers.push(trigger);
      }
    }

    #syncAriaRoles() {
      const isDesktop = this.#isDesktop;

      if (this.#triggersHost) {
        if (isDesktop) {
          this.#triggersHost.setAttribute('role', 'tablist');
        } else {
          this.#triggersHost.removeAttribute('role');
        }
      }

      for (const panel of this.#panels) {
        const trigger = this.#triggers.find(
          (item) => item.getAttribute('data-block-id') === panel.getAttribute('data-block-id')
        );
        const body = this.#findBody(panel);

        if (!trigger) continue;

        if (isDesktop) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', trigger.id);
          trigger.setAttribute('role', 'tab');
          trigger.setAttribute('aria-controls', panel.id);
          trigger.removeAttribute('aria-expanded');

          if (body) {
            body.removeAttribute('role');
            body.removeAttribute('aria-labelledby');
          }
        } else {
          // Accordion: header button controls a region body (not a nested tabpanel).
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
          panel.removeAttribute('aria-hidden');

          trigger.setAttribute('role', 'button');
          trigger.removeAttribute('aria-selected');

          if (body) {
            const bodyId = body.id || `${panel.id}-body`;

            body.id = bodyId;
            body.setAttribute('role', 'region');
            body.setAttribute('aria-labelledby', trigger.id);
            trigger.setAttribute('aria-controls', bodyId);
          }
        }
      }
    }

    #syncLayout() {
      if (!this.#triggersHost) return;

      if (this.#isDesktop) {
        this.dataset.layout = 'tabs';
        this.#triggersHost.replaceChildren(...this.#triggers);
        this.#triggersHost.hidden = false;
        this.#syncAriaRoles();
        return;
      }

      this.dataset.layout = 'accordion';
      this.#triggersHost.replaceChildren();
      this.#triggersHost.hidden = true;

      for (const panel of this.#panels) {
        const trigger = this.#triggers.find(
          (item) => item.getAttribute('data-block-id') === panel.getAttribute('data-block-id')
        );

        if (!trigger) continue;

        const body = this.#findBody(panel);

        if (body) {
          panel.insertBefore(trigger, body);
        } else {
          panel.prepend(trigger);
        }
      }

      this.#syncAriaRoles();
    }

    #bindEvents() {
      for (const trigger of this.#triggers) {
        trigger.addEventListener('click', this.#handleTriggerClick);
        trigger.addEventListener('keydown', this.#handleKeydown);
      }
    }

    #handleTriggerClick = (event) => {
      const trigger = event.currentTarget;
      const blockId = trigger.getAttribute('data-block-id');

      if (!blockId) return;

      // Mobile accordion: tapping the open header collapses all panels.
      if (!this.#isDesktop && blockId === this.#activeId) {
        this.#deactivateAll();
        return;
      }

      this.#activate(blockId);
    };

    #handleKeydown = (event) => {
      const desktopKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      const mobileKeys = ['ArrowUp', 'ArrowDown', 'Home', 'End'];
      const keys = this.#isDesktop ? desktopKeys : mobileKeys;

      if (!keys.includes(event.key)) return;

      event.preventDefault();

      const currentIndex = this.#triggers.indexOf(event.currentTarget);

      if (currentIndex < 0) return;

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % this.#triggers.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + this.#triggers.length) % this.#triggers.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.#triggers.length - 1;
      }

      const next = this.#triggers[nextIndex];

      if (!next) return;

      next.focus();
      this.#activate(next.getAttribute('data-block-id'));
    };

    #handleMediaQueryChange = () => {
      this.#syncLayout();

      if (this.#activeId) {
        this.#activate(this.#activeId);
        return;
      }

      // Desktop tabs need one selected panel. If mobile collapsed everything,
      // restore the first visible tab when crossing the breakpoint.
      if (this.#isDesktop) {
        const first = this.#visiblePanels()[0];

        if (first) this.#activate(first.getAttribute('data-block-id'));
      }
    };

    #handleBlockSelect = (event) => {
      if (!this.#isDesignMode || !event.target || !this.contains(event.target)) return;

      const panel = event.target.closest('[data-product-details-panel]');

      if (!panel || !this.contains(panel)) return;

      this.#activate(panel.getAttribute('data-block-id'));
    };

    #deactivateAll() {
      this.#activeId = null;

      for (const panel of this.#panels) {
        const isEmpty = panel.dataset.empty === 'true';

        if (!this.#isDesignMode && isEmpty) {
          panel.classList.remove('is-active');
          panel.hidden = true;
          continue;
        }

        panel.classList.remove('is-active');
        panel.hidden = false;
        panel.removeAttribute('aria-hidden');

        const body = this.#findBody(panel);

        if (body) body.setAttribute('aria-hidden', 'true');
      }

      for (const trigger of this.#triggers) {
        trigger.classList.remove('is-active');
        trigger.removeAttribute('aria-selected');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.tabIndex = 0;
      }
    }

    #activate(blockId) {
      if (!blockId) return;

      this.#activeId = blockId;

      const isDesktop = this.#isDesktop;

      for (const panel of this.#panels) {
        const isActive = panel.getAttribute('data-block-id') === blockId;
        const isEmpty = panel.dataset.empty === 'true';

        if (!this.#isDesignMode && isEmpty) {
          panel.classList.remove('is-active');
          panel.hidden = true;
          continue;
        }

        panel.classList.toggle('is-active', isActive);
        panel.hidden = false;

        const body = this.#findBody(panel);

        if (isDesktop) {
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (body) body.removeAttribute('aria-hidden');
        } else {
          panel.removeAttribute('aria-hidden');
          if (body) body.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        }
      }

      for (const trigger of this.#triggers) {
        const isActive = trigger.getAttribute('data-block-id') === blockId;

        trigger.classList.toggle('is-active', isActive);

        if (isDesktop) {
          trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
          trigger.removeAttribute('aria-expanded');
          trigger.tabIndex = isActive ? 0 : -1;
        } else {
          trigger.removeAttribute('aria-selected');
          trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
          trigger.tabIndex = 0;
        }
      }
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, ProductDetails);
  }
})();
